import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import { EpocusSeedKind, EpocusSeedSource } from "elephant-web/gql";
import { type FormEvent, useState } from "react";
import { EPOCUS_TEXT_SECONDARY, rem } from "../../_lib/epocus-palette";
import { useSeedEpocusDemoStudiesMutation } from "../../settings/_lib/__generated__/epocus-settings-operations.gql";
import { useEpocusAdminCreateProgramMutation } from "./__generated__/epocus-admin-operations.gql";
import { CreatedProgramCallout } from "./created-program-callout";

export const DEMO_STUDY_COUNTS = [10, 50, 100] as const;
export const SEED_BATCH_SIZE = 10;

type DemoStudyCount = (typeof DEMO_STUDY_COUNTS)[number];

type CreatedProgram = {
  id: string;
  name: string;
  dimseAeTitle?: string | null;
};

type Stage =
  | { status: "form" }
  | { status: "creating" }
  | { status: "seeding"; programId: string; seeded: number; target: number }
  | { status: "done"; program: CreatedProgram; seeded: number; target: number };

const Fields = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const HelperCaption = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const ProgressBlock = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

export const CreateDemoProgramDialog: FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const [name, setName] = useState("");
  const [count, setCount] = useState<DemoStudyCount>(10);
  const [nameError, setNameError] = useState<string>();
  const [stage, setStage] = useState<Stage>({ status: "form" });

  const [createProgram] = useEpocusAdminCreateProgramMutation({
    refetchQueries: ["EpocusAdminPrograms"],
  });
  const [seedDemoStudies] = useSeedEpocusDemoStudiesMutation();

  const busy = stage.status === "creating" || stage.status === "seeding";

  const reset = () => {
    setName("");
    setCount(10);
    setNameError(undefined);
    setStage({ status: "form" });
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Program name is required");
      return;
    }
    setNameError(undefined);
    setStage({ status: "creating" });

    let program: CreatedProgram;
    try {
      const { data } = await createProgram({
        variables: { input: { name: trimmedName } },
      });
      if (!data) throw new Error("No program returned");
      program = data.createEpocusProgram;
    } catch (error) {
      toast.error(
        `Couldn't create program: ${error instanceof Error ? error.message : "unknown error"}`,
      );
      setStage({ status: "form" });
      return;
    }

    let seeded = 0;
    setStage({
      status: "seeding",
      programId: program.id,
      seeded,
      target: count,
    });
    while (seeded < count) {
      try {
        const { data } = await seedDemoStudies({
          variables: {
            programId: program.id,
            count: Math.min(SEED_BATCH_SIZE, count - seeded),
            kind: EpocusSeedKind.Both,
            source: EpocusSeedSource.Library,
            deviceSerialNumber: null,
            examTypes: null,
          },
        });
        const landed = data?.seedEpocusDemoStudies.length ?? 0;
        if (landed === 0) break;
        seeded += landed;
        setStage({
          status: "seeding",
          programId: program.id,
          seeded,
          target: count,
        });
      } catch (error) {
        toast.error(
          `Seeding stopped after ${seeded} studies: ${error instanceof Error ? error.message : "unknown error"}`,
        );
        break;
      }
    }
    setStage({ status: "done", program, seeded, target: count });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {stage.status === "done" ? "Demo program ready" : "New demo program"}
      </DialogTitle>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <DialogContent>
          {stage.status === "done" ? (
            <ProgressBlock>
              <CreatedProgramCallout
                programId={stage.program.id}
                name={stage.program.name}
                dimseAeTitle={stage.program.dimseAeTitle}
              />
              <HelperCaption role="status">
                {stage.seeded === stage.target
                  ? `Seeded ${stage.seeded} studies from the seed library. Add a performer to start documenting them.`
                  : `Seeded ${stage.seeded} of ${stage.target} studies before seeding stopped. You can add more from the program page.`}
              </HelperCaption>
            </ProgressBlock>
          ) : stage.status === "seeding" ? (
            <ProgressBlock>
              <Typography style={{ fontSize: rem(13) }}>
                Seeding {stage.target} studies…
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(stage.seeded / stage.target) * 100}
                aria-label="Seeding progress"
              />
              <HelperCaption role="status">
                {stage.seeded} of {stage.target} studies seeded
              </HelperCaption>
            </ProgressBlock>
          ) : (
            <Fields>
              <TextField
                id="epocus-admin-demo-program-name"
                label="Program name"
                required
                autoFocus
                variant="outlined"
                fullWidth
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError(undefined);
                }}
                error={!!nameError}
                helperText={nameError}
                disabled={busy}
              />
              <Box display="flex" alignItems="center" gridGap={12}>
                <Typography style={{ fontSize: rem(13) }}>
                  Studies to seed
                </Typography>
                <Select
                  variant="outlined"
                  margin="dense"
                  value={count}
                  onChange={(event) =>
                    setCount(Number(event.target.value) as DemoStudyCount)
                  }
                  inputProps={{ "aria-label": "Studies to seed" }}
                  style={{ minWidth: 88 }}
                  disabled={busy}
                >
                  {DEMO_STUDY_COUNTS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <HelperCaption>
                Creates a standalone program with every exam type enabled and
                fills its To document queue with unclaimed studies from the seed
                library, images matched to exam type. Marked as test scans, so
                they stay out of QA numbers.
              </HelperCaption>
            </Fields>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={busy}
            style={{ textTransform: "none" }}
          >
            {stage.status === "done" ? "Done" : "Cancel"}
          </Button>
          {stage.status === "form" && (
            <Button type="submit" variant="contained" color="primary">
              Create and seed
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};
